import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];
const DAY_NAMES = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const CDateTimePicker = ({
  cId, cHelperText, cTooltipText, cDisabled,
  cOnChange, cKeyReset, cIsSession, cHideTime, cSmall, cLabel,
}) => {
  const getInitialDate = () => {
    if (cIsSession) {
      const stored = sessionStorage.getItem(cId);
      return stored ? new Date(stored) : null;
    }
    return null;
  };

  const today = new Date();
  const [isOpen, setIsOpen]             = useState(false);
  const [viewMonth, setViewMonth]       = useState(today.getMonth());
  const [viewYear, setViewYear]         = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState(getInitialDate);
  const [popupPos, setPopupPos]         = useState({});
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const popup = document.getElementById(`cdtp-${cId}`);
      if (
        wrapperRef.current && !wrapperRef.current.contains(e.target) &&
        popup && !popup.contains(e.target)
      ) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [cId]);

  useEffect(() => {
    if (cKeyReset) setSelectedDate(null);
  }, [cKeyReset]);

  useEffect(() => {
    if (cIsSession) {
      const stored = sessionStorage.getItem(cId);
      if (stored) setSelectedDate(new Date(stored));
    }
  }, [cIsSession, cId]);

  // ✅ Display value — always shows date only, no time
  const formatDisplay = (date) => {
    if (!date) return "";
    const yyyy = date.getFullYear();
    const mm   = String(date.getMonth() + 1).padStart(2, "0");
    const dd   = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // ✅ Output value — sends full datetime to cOnChange
  const formatOutput = (date) => {
    if (!date) return "";
    const yyyy = date.getFullYear();
    const mm   = String(date.getMonth() + 1).padStart(2, "0");
    const dd   = String(date.getDate()).padStart(2, "0");
    return cHideTime ? `${yyyy}-${mm}-${dd}` : `${yyyy}-${mm}-${dd}T00:00`;
  };

  const handleOpen = () => {
    if (cDisabled) return;
    if (isOpen) { setIsOpen(false); return; }
    if (wrapperRef.current) {
      const rect   = wrapperRef.current.getBoundingClientRect();
      const popupH = 340;
      const popupW = 285;
      setPopupPos({
        top:  window.innerHeight - rect.bottom < popupH ? rect.top - popupH - 6 : rect.bottom + 6,
        left: window.innerWidth  - rect.left   < popupW ? rect.right - popupW   : rect.left,
      });
    }
    setIsOpen(true);
  };

  // ✅ Always closes on day click, no time box shown
  const handleDayClick = (day) => {
    const date = new Date(viewYear, viewMonth, day);
    setSelectedDate(date);
    const output = formatOutput(date);
    cOnChange(output);
    sessionStorage.setItem(cId, output);
    setIsOpen(false);
  };

  // ✅ Quick shortcuts also always close
  const setQuickDate = (offsetDays) => {
    const date = new Date();
    date.setDate(date.getDate() + offsetDays);
    setSelectedDate(date);
    setViewMonth(date.getMonth());
    setViewYear(date.getFullYear());
    const output = formatOutput(date);
    cOnChange(output);
    sessionStorage.setItem(cId, output);
    setIsOpen(false);
  };

  // ✅ Only updates month view — never closes calendar
  const changeMonth = (dir) => {
    setViewMonth(prev => {
      let m = prev + dir;
      if (m > 11) { setViewYear(y => y + 1); return 0; }
      if (m < 0)  { setViewYear(y => y - 1); return 11; }
      return m;
    });
  };

  const firstDay    = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const displayValue = formatDisplay(selectedDate);

  const S = {
    trigger: {
      display: "flex", alignItems: "center", gap: 10,
      border: `0.5px solid ${isOpen ? "#378ADD" : "#d0d0d0"}`,
      borderRadius: 8,
      padding: cSmall ? "6px 10px" : "10px 14px",
      cursor: cDisabled ? "not-allowed" : "pointer",
      background: cDisabled ? "#f9f9f9" : "#fff",
      opacity: cDisabled ? 0.55 : 1,
      boxShadow: isOpen ? "0 0 0 3px rgba(55,138,221,0.13)" : "none",
      transition: "border-color 0.15s, box-shadow 0.15s",
      userSelect: "none",
    },
    popup: {
      position: "fixed",
      top: popupPos.top,
      left: popupPos.left,
      background: "#fff",
      border: "0.5px solid #e0e0e0",
      borderRadius: 12,
      padding: 16,
      zIndex: 99999,
      width: 285,
      boxShadow: "0 8px 32px rgba(0,0,0,0.11)",
    },
    navBtn: {
      background: "none",
      border: "0.5px solid #ebebeb",
      borderRadius: 7,
      width: 30, height: 30,
      cursor: "pointer",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 15, color: "#666",
    },
    day: (isSel, isToday) => ({
      textAlign: "center",
      padding: "7px 0",
      borderRadius: 7,
      fontSize: 13,
      cursor: "pointer",
      background: isSel ? "#378ADD" : "transparent",
      color: isSel ? "#fff" : isToday ? "#378ADD" : "#333",
      border: isToday && !isSel ? "1px solid #378ADD" : "1px solid transparent",
      fontWeight: isSel ? 500 : 400,
      transition: "background 0.1s",
    }),
    quickBtn: {
      fontSize: 11, fontWeight: 500,
      padding: "4px 12px",
      border: "0.5px solid #e0e0e0",
      borderRadius: 20,
      cursor: "pointer",
      background: "#f5f7fa",
      color: "#555",
    },
  };

  const popup = (
    <div id={`cdtp-${cId}`} style={S.popup}>

      {/* ── Quick shortcuts */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {[["Today", 0], ["Tomorrow", 1], ["In 7 days", 7]].map(([label, offset]) => (
          <button
            key={label}
            style={S.quickBtn}
            onClick={() => setQuickDate(offset)}
            onMouseEnter={e => Object.assign(e.currentTarget.style, { background: "#E6F1FB", color: "#185FA5", borderColor: "#B5D4F4" })}
            onMouseLeave={e => Object.assign(e.currentTarget.style, { background: "#f5f7fa", color: "#555", borderColor: "#e0e0e0" })}
          >{label}</button>
        ))}
      </div>

      {/* ── Month navigation */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <button
          style={S.navBtn}
          onClick={() => changeMonth(-1)}
          aria-label="Previous month"
          onMouseEnter={e => e.currentTarget.style.background = "#f5f5f5"}
          onMouseLeave={e => e.currentTarget.style.background = "none"}
        >&#8249;</button>
        <span style={{ fontWeight: 500, fontSize: 14, color: "#222" }}>
          {MONTHS[viewMonth]} {viewYear}
        </span>
        <button
          style={S.navBtn}
          onClick={() => changeMonth(1)}
          aria-label="Next month"
          onMouseEnter={e => e.currentTarget.style.background = "#f5f5f5"}
          onMouseLeave={e => e.currentTarget.style.background = "none"}
        >&#8250;</button>
      </div>

      {/* ── Day grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3 }}>
        {DAY_NAMES.map(d => (
          <div key={d} style={{ textAlign: "center", fontSize: 11, color: "#bbb", padding: "4px 0", fontWeight: 500 }}>{d}</div>
        ))}
        {Array(firstDay).fill(null).map((_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
          const isToday = day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();
          const isSelected = selectedDate &&
            day === selectedDate.getDate() &&
            viewMonth === selectedDate.getMonth() &&
            viewYear === selectedDate.getFullYear();
          return (
            <div
              key={day}
              style={S.day(isSelected, isToday)}
              onClick={() => handleDayClick(day)}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "#EEF5FD"; }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
            >{day}</div>
          );
        })}
      </div>

    </div>
  );

  return (
    <div ref={wrapperRef} style={{ width: "100%" }}>

      {/* ── Label */}
      {cLabel && (
        <div style={{ fontSize: 12, fontWeight: 500, color: "#888", marginBottom: 6, letterSpacing: "0.03em" }}>
          {cLabel}
        </div>
      )}

      {/* ── Trigger */}
      <div style={S.trigger} onClick={handleOpen} title={cTooltipText}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#378ADD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="4" width="18" height="18" rx="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        <span style={{ flex: 1, fontSize: cSmall ? 13 : 13.5, color: displayValue ? "#222" : "#bbb" }}>
          {displayValue || "Select date"}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2.5" strokeLinecap="round"
          style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} aria-hidden="true">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>

      {/* ── Helper text */}
      {cHelperText && (
        <p style={{ fontSize: 11, color: "#bbb", marginTop: 5 }}>{cHelperText}</p>
      )}

      {/* ── Portal */}
      {isOpen && createPortal(popup, document.body)}

    </div>
  );
};

export default CDateTimePicker;